import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import type { StandardCode } from '@/types/standards';

export interface Customer {
  id: string;
  /** DB integer primary key — always use this for server API calls when present */
  serverId?: number;
  name: string;
  company: string;
  contactEmail: string;
  phone: string;
  address: string;
  notes: string;
  createdAt: string;
  // Regulatory profile
  country?: 'AU' | 'GB' | 'US' | 'CA' | 'NZ' | 'IE' | 'DE' | 'FR' | 'SG' | 'OTHER';
  industryType?: string;
  standard?: string; // StandardCode — derived from country
}

export interface UserProfile {
  companyName: string;
  fullName: string;
  jobTitle: string;
  email: string;
  phone: string;
  address: string;
  abn?: string;
  licenseNumber?: string;
  companyRegNumber?: string; // UK Companies House
  vatNumber?: string;
  country?: 'GB' | 'AU' | 'US' | 'CA' | 'NZ' | 'IE' | 'DE' | 'FR' | 'SG' | 'OTHER';
}

interface SessionState {
  currentCustomer: Customer | null;
  userProfile: UserProfile | null;
  activeStandard: StandardCode;
  setCurrentCustomer: (c: Customer | null) => void;
  setUserProfile: (p: UserProfile | null) => void;
  setActiveStandard: (s: StandardCode) => void;
  clearSession: () => void;
  syncStandardFromBackend: () => Promise<void>;
}

const SESSION_KEY = 'rb_session_v2';
const STANDARD_KEY = 'rb_active_standard';

async function apiSaveStandard(standard: StandardCode) {
  const token = localStorage.getItem('rb_api_token');
  if (!token) return;
  const base = localStorage.getItem('rb_api_url') || '/api';
  try {
    await fetch(base + '/auth/settings.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'save', standard, __token: token }),
    });
  } catch { /* ignore */ }
}

async function apiLoadStandard(): Promise<StandardCode | null> {
  const token = localStorage.getItem('rb_api_token');
  if (!token) return null;
  const base = localStorage.getItem('rb_api_url') || '/api';
  try {
    const res = await fetch(base + '/auth/settings.php?action=get', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'get', __token: token }),
    });
    const data = await res.json();
    if (data?.success && data?.standard) return data.standard as StandardCode;
  } catch { /* ignore */ }
  return null;
}

function loadSession(): { currentCustomer: Customer | null; userProfile: UserProfile | null; activeStandard: StandardCode } {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        currentCustomer: parsed.currentCustomer ?? null,
        userProfile: parsed.userProfile ?? null,
        activeStandard: parsed.activeStandard ?? 'UK_EA',
      };
    }
  } catch { /* ignore */ }
  // Fallback to old standard key
  try {
    const std = localStorage.getItem(STANDARD_KEY) as StandardCode | null;
    if (std) return { currentCustomer: null, userProfile: null, activeStandard: std };
  } catch { /* ignore */ }
  return { currentCustomer: null, userProfile: null, activeStandard: 'UK_EA' };
}

function saveSession(session: { currentCustomer: Customer | null; userProfile: UserProfile | null; activeStandard: StandardCode }) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    localStorage.setItem(STANDARD_KEY, session.activeStandard);
  } catch { /* ignore */ }
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const initial = loadSession();
  const [currentCustomer, setCurrentCustomerState] = useState<Customer | null>(initial.currentCustomer);
  const [userProfile, setUserProfileState] = useState<UserProfile | null>(initial.userProfile);
  const [activeStandard, setActiveStandardState] = useState<StandardCode>(initial.activeStandard);

  const setCurrentCustomer = useCallback((c: Customer | null) => {
    setCurrentCustomerState(c);
  }, []);

  const setUserProfile = useCallback((p: UserProfile | null) => {
    setUserProfileState(p);
  }, []);

  const setActiveStandard = useCallback((s: StandardCode) => {
    setActiveStandardState(s);
    try { localStorage.setItem(STANDARD_KEY, s); } catch { /* ignore */ }
    // Also save to backend if authenticated
    apiSaveStandard(s);
  }, []);

  const clearSession = useCallback(() => {
    setCurrentCustomerState(null);
    setUserProfileState(null);
    // Purge stale customer list from localStorage so the next user doesn't
    // see the previous user's customers while the fresh API call loads.
    try { localStorage.removeItem('rb_customers'); } catch { /* ignore */ }
    // Keep the active standard — it should persist across user sessions
  }, []);

  const syncStandardFromBackend = useCallback(async () => {
    const backendStandard = await apiLoadStandard();
    if (backendStandard) {
      setActiveStandardState(backendStandard);
      try { localStorage.setItem(STANDARD_KEY, backendStandard); } catch { /* ignore */ }
    }
  }, []);

  // On mount, try to sync standard from backend if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('rb_api_token');
    if (token) {
      syncStandardFromBackend();
    }
  }, [syncStandardFromBackend]);

  // Listen for login success events — clear previous user's session, then sync standard
  useEffect(() => {
    const handler = () => {
      clearSession();           // wipe any stale customer/profile from previous user
      syncStandardFromBackend(); // then load this user's preferred standard
    };
    window.addEventListener('rb-login-success', handler);
    return () => window.removeEventListener('rb-login-success', handler);
  }, [clearSession, syncStandardFromBackend]);

  // Listen for logout events — clear customer/profile so they don't bleed into the next session
  useEffect(() => {
    const handler = () => { clearSession(); };
    window.addEventListener('rb-logout', handler);
    return () => window.removeEventListener('rb-logout', handler);
  }, [clearSession]);

  // Persist on every change
  useEffect(() => {
    saveSession({ currentCustomer, userProfile, activeStandard });
  }, [currentCustomer, userProfile, activeStandard]);

  const value = useMemo(() => ({
    currentCustomer, userProfile, activeStandard,
    setCurrentCustomer, setUserProfile, setActiveStandard, clearSession, syncStandardFromBackend,
  }), [currentCustomer, userProfile, activeStandard, setCurrentCustomer, setUserProfile, setActiveStandard, clearSession, syncStandardFromBackend]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
